import React from 'react';
import { Chip, Stack } from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import type { TutorSessionType } from '../../models/Tutor';
import {
  getTutorSessionTypeLabel,
  isAtStudentSession,
  isOnlineSession,
  normalizeTutorSessionType,
} from '../../utils/tutorSessionType';
import TutorSessionTypeHeading from './TutorSessionTypeHeading';
import { passMetaChipSx } from '../passes/passDetailLayout';

export interface TutorLessonSessionTypeChipProps {
  sessionType?: TutorSessionType | 'OFFLINE' | null;
  size?: 'small' | 'medium';
  /** Section heading + chip — use on detail screens, not dense lists. */
  showLabel?: boolean;
}

const fitContentChipSx = {
  width: 'fit-content',
  maxWidth: '100%',
};

const TutorLessonSessionTypeChip: React.FC<TutorLessonSessionTypeChipProps> = ({
  sessionType,
  size = 'small',
  showLabel = false,
}) => {
  const { t } = useTranslation();
  const normalized = normalizeTutorSessionType(sessionType);
  if (!normalized) return null;

  const icon = isOnlineSession(normalized) ? (
    <VideocamOutlinedIcon aria-hidden />
  ) : isAtStudentSession(normalized) ? (
    <HomeOutlinedIcon aria-hidden />
  ) : (
    <PlaceOutlinedIcon aria-hidden />
  );

  const chip = (
    <Chip
      size={size}
      variant="outlined"
      color={isOnlineSession(normalized) ? 'primary' : 'default'}
      icon={icon}
      label={getTutorSessionTypeLabel(normalized, t)}
      sx={size === 'small' ? passMetaChipSx : fitContentChipSx}
    />
  );

  if (!showLabel) {
    return chip;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="center"
      flexWrap="wrap"
      useFlexGap
    >
      <TutorSessionTypeHeading variant="type" />
      {chip}
    </Stack>
  );
};

export default TutorLessonSessionTypeChip;
